import type { HardwareAlone, HardwareInteraction, TableHole } from './hardwareInteractionsRun';
import { holeNumber, type StepHole } from './stepHoles';

// THE HOLE IDS A NEW TABLE KEEPS FROM THE LAST (Louis 2026-10-29). The call numbers a step's runs in the
// order of its entries' first sub-steps, so a Phase 4 run that moves a gesture renumbers them: on the
// Josette, the right screw was H202 when Find holes placed it (17 Oct) and H206 in the table read three
// days later, while its holes kept H202 — the table and the wood naming one hole two ways. A line the
// last table had (same hardware, same parts, same hand; same part, hardware and kind), or whose holes
// are placed already (same part, hardware and hand), keeps its number; a new one takes the next free.

type Row = Pick<HardwareInteraction, 'components' | 'side' | 'holes'> & { hardware: string[] | string };
const rowKey = (r: Row) =>
  `${[r.hardware].flat().slice().sort().join(',')}|${[...r.components].sort().join(',')}|${r.side ?? ''}`;
const lineKey = (h: TableHole) => `${h.part}|${h.for}|${h.kind}`;

export function keepHoleIds<T extends HardwareInteraction | HardwareAlone>(next: T[], prev: Row[], placed: StepHole[]): T[] {
  const before = new Map(prev.map((r) => [rowKey(r), r]));
  // NEW NUMBER -> OLD, line by line, id by id: first the holes Find holes placed and the drill cut —
  // what the wood is called — then the last table's.
  const to = new Map<number, number>();
  const link = (id: string, was: string | undefined) => {
    const a = holeNumber(id);
    const b = holeNumber(was ?? '');
    if (Number.isFinite(a) && Number.isFinite(b) && !to.has(a) && ![...to.values()].includes(b)) to.set(a, b);
  };
  // THE PLACED RUN THAT IS THIS ONE: a number whose holes are on every part and of every hardware this
  // run's are, on its hand where a hole says one — the screw's hole in the legs says none, its hole in
  // the right rail says right. Taken in id order, each once.
  const placedRuns = new Map<number, StepHole[]>();
  placed.forEach((o) => placedRuns.set(holeNumber(o.id), [...(placedRuns.get(holeNumber(o.id)) ?? []), o]));
  const taken = new Set<number>();
  for (const r of next) {
    const runs = new Map<number, TableHole[]>();
    (r.holes ?? []).forEach((h) => (h.ids ?? []).forEach((id) => {
      const n = holeNumber(id);
      if (Number.isFinite(n)) runs.set(n, [...(runs.get(n) ?? []), h]);
    }));
    for (const [n, lines] of runs) {
      if (to.has(n)) continue;
      const fits = [...placedRuns.entries()].filter(([m, holes]) => !taken.has(m) && lines.every((h) => holes.some((o) =>
        o.part === h.part && o.via === h.for && (!r.side || !o.side || o.side === r.side))))
        .sort(([a], [b]) => a - b);
      if (!fits.length) continue;
      to.set(n, fits[0][0]);
      taken.add(fits[0][0]);
    }
  }
  // AND THE LAST TABLE'S, for a run nothing is placed for yet: the same line in the same row.
  for (const r of next) {
    const seen = new Map<string, number>();
    for (const h of r.holes ?? []) {
      const nth = seen.get(lineKey(h)) ?? 0;
      seen.set(lineKey(h), nth + 1);
      const was = (before.get(rowKey(r as Row))?.holes ?? []).filter((o) => lineKey(o) === lineKey(h))[nth]?.ids;
      if (was) (h.ids ?? []).forEach((id, i) => link(id, was[i]));
    }
  }
  if (!to.size) return next;
  // THE OTHERS, past every number now used in their step: never one a kept line holds.
  const used = new Set(to.values());
  const all = [...new Set(next.flatMap((r) => (r.holes ?? []).flatMap((h) => h.ids ?? [])).map(holeNumber)
    .filter(Number.isFinite))].sort((a, b) => a - b);
  for (const n of all) {
    if (to.has(n)) continue;
    if (!used.has(n)) { to.set(n, n); used.add(n); continue; }
    let free = Math.floor(n / 100) * 100 + 1;
    while (used.has(free) || all.includes(free) && !to.has(free)) free += 1;
    to.set(n, free);
    used.add(free);
  }
  const rename = (id: string) => {
    const n = holeNumber(id);
    return Number.isFinite(n) && to.has(n) ? id.replace(/^H\d+/i, `H${to.get(n)}`) : id;
  };
  return next.map((r) => ({ ...r, holes: (r.holes ?? []).map((h) => (h.ids ? { ...h, ids: h.ids.map(rename) } : h)) }));
}
